/**
 * Dashboard Screen (Homepage)
 * Welcome page with greeting, My Rituality hero card, chat input, and navigation cards
 * Follows the dark theme design with image cards and gradient overlays
 */

import { Mic, SendHorizontal } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { CreateModal } from "@/components/modals/CreateModal"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useCreateAiConversation } from "@/features/chats/hooks/useAiChat"
import { useSpeechRecognition } from "@/hooks/useSpeechRecognition"
import { cn } from "@/lib/utils"
import { getAssetUrl } from "@/utils/assets"

interface NavigationCard {
  id: string
  title: string
  path: string
  image?: string
}

export function DashboardScreen() {
  const user = useAuthStore(state => state.user)
  const navigate = useNavigate()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [chatInput, setChatInput] = useState("")
  const createConversation = useCreateAiConversation()

  const inputRef = useRef(chatInput)
  useEffect(() => {
    inputRef.current = chatInput
  }, [chatInput])
  const handleTranscript = useCallback((text: string) => {
    const current = inputRef.current
    const next = current ? `${current.trimEnd()} ${text.trim()}` : text.trim()
    setChatInput(next)
  }, [])
  const speech = useSpeechRecognition({ onTranscript: handleTranscript })

  const navigationCards: NavigationCard[] = [
    {
      id: "learn-practice",
      title: "LEARN & PRACTICE",
      path: "/learn-and-practice",
      image: getAssetUrl("learning_pract.jpeg"),
    },
    {
      id: "connect",
      title: "CONNECT",
      path: "/connect",
      image: getAssetUrl("connect.jpeg"),
    },
    {
      id: "book-expert",
      title: "BOOK AN EXPERT",
      path: "/connect/experts",
      image: getAssetUrl("book_expert.jpeg"),
    },
    {
      id: "create",
      title: "CREATE",
      path: "/rituals/create",
      image: getAssetUrl("create.jpeg"),
    },
  ]

  const dashboardPrimaryImage = getAssetUrl("dashboard_primary.png")

  const handleChatSend = async () => {
    const message = chatInput.trim()
    if (!message || createConversation.isPending) return

    try {
      const conversation = await createConversation.mutateAsync()
      navigate(`/chats/ai/${conversation.id}?firstMessage=${encodeURIComponent(message)}`)
    } catch {
      toast.error("Failed to start conversation")
    }
  }

  return (
    <div className="min-h-screen space-y-8 md:space-y-12 max-w-360 mx-auto px-5 md:px-17.5">
      {/* Welcome Section */}
      <div className="hidden md:flex flex-col items-center mt-10 md:mt-15 md:mt-24">
        <h1 className="text-[25px] md:text-[40px] font-semibold leading-8 md:leading-16.25 text-primary uppercase text-center mb-10 sm:mb-0">
          Welcome {user?.name || ""}
        </h1>
        <p className="text-color-brand-offwhite text-sm leading-6.25 text-center">
          Everything you need to explore, practice, and grow through Ritual is right here.
        </p>
      </div>

      {/* Content Container */}
      <div className="pb-10 pt-1">
        {/* My Rituality Hero Card */}
        <div
          className="relative min-h-60 w-full rounded-tl-[60px] overflow-hidden cursor-pointer group"
          onClick={() => navigate("/my-rituality")}
        >
          {/* Rituality Logo */}
          <img
            src={dashboardPrimaryImage}
            alt="Rituality Logo"
            className="object-cover w-full h-60 md:h-105 z-10 blur-[10px]"
          />

          {/* Title */}
          <div className="absolute bottom-4 left-4 md:bottom-7 md:left-8 z-10">
            <h2 className="text-[15px] sm:text-[20px] md:text-[30px] font-semibold text-white leading-normal uppercase font-monteserat">
              My Rituality
            </h2>
          </div>
        </div>

        {/* Chat Input Section */}
        <div className="w-full border border-color-brand-offwhite p-5 md:p-7.5 flex flex-col gap-5 mb-5 md:mb-7.5 rounded-br-[60px]">
          <p className="text-color-brand-offwhite text-sm mb-0">
            What feels present for you right now?
          </p>
          <form
            onSubmit={e => {
              e.preventDefault()
              handleChatSend()
            }}
            className="relative flex items-center"
          >
            <input
              type="text"
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              disabled={createConversation.isPending}
              placeholder="Type here..."
              className="w-[calc(100%-20px)] px-5 pl-7 sm:pr-25 pr-20 rounded-[15px] h-10 md:h-17.5 bg-white/7 border border-[rgba(242, 240, 235, 0.15)] text-primary placeholder:text-body-ultra.c text-xs md:text-[14px] leading-6.25 focus:outline-none disabled:opacity-50"
            />
            <div className="absolute right-8 flex items-center gap-0">
              {speech.isSupported && (
                <button
                  type="button"
                  onClick={speech.toggle}
                  disabled={createConversation.isPending}
                  aria-label={speech.isRecording ? "Stop recording" : "Voice input"}
                  aria-pressed={speech.isRecording}
                  className={cn(
                    "p-2 rounded-full transition-colors disabled:opacity-30 disabled:cursor-not-allowed",
                    speech.isRecording
                      ? "text-red-500 animate-pulse"
                      : "text-primary hover:text-primary/80",
                  )}
                >
                  <Mic className="w-4 h-4 md:w-6.25 md:h-6.25" />
                </button>
              )}
              <button
                type="submit"
                disabled={!chatInput.trim() || createConversation.isPending}
                className="p-2 rounded-full cursor-pointer disabled:opacity-30"
                aria-label="Send message"
              >
                <SendHorizontal className="w-4 h-4 md:w-6.25 md:h-6.25 text-primary" />
              </button>
            </div>
          </form>
        </div>

        {/* Navigation Cards Grid - 2x2 */}
        <div className="grid grid-cols-2 gap-4 md:gap-7.5 md:mb-10 mb-0">
          {navigationCards.map(card => (
            <div
              key={card.id}
              className="relative h-60 sm:h-80 md:h-100 rounded-tl-[60px] rounded-br-[60px] overflow-hidden cursor-pointer group"
              onClick={() =>
                card.id === "create" ? setShowCreateModal(true) : navigate(card.path)
              }
              role="link"
              tabIndex={0}
            >
              {/* Background Image */}
              {card.image && (
                <img src={card.image} alt={card.title} className="w-full h-full object-cover" />
              )}

              {/* Title */}
              <div className="absolute left-5 bottom-5 md:bottom-7.5 md:left-7.5 z-10 max-w-[80px]">
                <h3 className="text-[15px] sm:text-[20px] md:text-[30px] font-semibold text-white uppercase">
                  {card.title}
                </h3>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Create Modal */}
      <CreateModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onNavigate={path => navigate(path)}
      />
    </div>
  )
}
